import { usePageContext } from '@providers/PageMetaProvider/model/PageContext';
import { GenerationProgressDashboard } from '@shared/components/GenerationProgressDashboard';
import { useGenerationProgress } from '@shared/hooks/useGenerationProgress';

/**
 * @description Progress dashboard for Demand Letter generation.
 * Replaces the old simple loading modal with real-time progress tracking.
 */

function DemandGeneratingModal() {
  const { values, setValues } = usePageContext();

  // All three popups are always mounted and share generatingDocumentId, so
  // each one only tracks it while its own popup is open. Otherwise the demand
  // popup asked /document-instances/<report id>/progress/ for every report
  // (ZLAYT-1514).
  const documentId = values?.generateModalOpened
    ? values?.generatingDocumentId || ''
    : '';
  const documentType = values?.generatingDocumentType || 'Demand Letter';
  const autoMinimize = values?.progressModalAutoMinimize;
  const expandedSignal = values?.progressModalExpandedAt;

  const progressData = useGenerationProgress(documentId, {
    enableSnapshot: true,
    // Poll the progress endpoint as a fallback so the bar still advances when
    // WebSocket task/progress events don't arrive (dropped socket, backgrounded
    // tab, etc.). The hook only polls while there are no live events and live
    // data always wins, so this is purely additive. See ZLAYT-1390.
    enablePolling: true,
    enableSynthetic: true,
    pipeline: 'demand_letter',
  });

  const onClose = () => {
    if (setValues) {
      setValues((prev) => ({
        ...prev,
        generateModalOpened: false,
        generatingDocumentId: undefined,
        generatingDocumentType: undefined,
        progressModalAutoMinimize: true,
        progressModalExpandedAt: undefined,
      }));
    }
  };

  // Don't render if modal is not opened or no document ID
  if (!values?.generateModalOpened || !documentId) {
    return null;
  }

  return (
    <GenerationProgressDashboard
      documentId={documentId}
      documentType={documentType}
      {...progressData}
      onClose={onClose}
      autoMinimize={autoMinimize}
      expandedSignal={expandedSignal}
      // ZLAYT-1642: handle_generate_document_instance_error refunds a failed
      // letter unless it was already delivered (ZLAYT-1526).
      chargeReturnedOnFailure
    />
  );
}

export default DemandGeneratingModal;
